import { Api } from '@shared-providers/api.provider';
import { Injectable } from '@angular/core';
import { Provider } from '@shared-providers/Provider';
import { LoadingService } from '@shared-services/loading.service';
import { CommunicationMessageType, ICommunicationTemplate } from '@internal-models/communication-template.model';

@Injectable({
	providedIn: 'root',
})
export class CommunicationTemplateProvider extends Provider<ICommunicationTemplate> {
	protected readonly url: string = '/communication-templates';

	constructor(
		protected readonly api: Api,
		protected readonly loadingService: LoadingService
	) {
		super(loadingService);
	}

	public async getAll(): Promise<Array<ICommunicationTemplate>> {
		return this.process(this.api.get(`${this.url}`)) as Promise<Array<ICommunicationTemplate>>;
	}

	public async getAllForCustomMessageType(
		messageType: CommunicationMessageType.CustomLessonReservation | CommunicationMessageType.CustomReservation
	): Promise<Array<ICommunicationTemplate>> {
		return this.process(this.api.get(`${this.url}/message-type?messageType=${messageType}`)) as Promise<
			Array<ICommunicationTemplate>
		>;
	}

	public async getOne(templateId: string): Promise<{ template: ICommunicationTemplate; variables: Array<string> }> {
		return this.process(this.api.get(`${this.url}/${templateId}`)) as Promise<{
			template: ICommunicationTemplate;
			variables: Array<string>;
		}>;
	}

	public getVariables(
		messageType: CommunicationMessageType.CustomLessonReservation | CommunicationMessageType.CustomReservation
	): Promise<Array<string>> {
		return this.process(this.api.get(`${this.url}/variables?messageType=${messageType}`)) as Promise<Array<string>>;
	}

	public getHtmlExample(html: string, messageType: CommunicationMessageType): Promise<string> {
		return this.process(this.api.put(`${this.url}/example`, { html, messageType })) as unknown as Promise<string>;
	}

	public updateStandard(
		templateId: string,
		template: Partial<ICommunicationTemplate>
	): Promise<ICommunicationTemplate> {
		return this.process(this.api.put(`${this.url}/${templateId}`, template)) as Promise<ICommunicationTemplate>;
	}

	public updateIsEnabled(templateId: string, isEnabled: boolean): Promise<ICommunicationTemplate> {
		return this.process(
			this.api.put(`${this.url}/${templateId}/is-enabled`, { isEnabled })
		) as Promise<ICommunicationTemplate>;
	}

	public updateCustom(
		templateId: string,
		template: Partial<ICommunicationTemplate>
	): Promise<ICommunicationTemplate> {
		return this.process(
			this.api.put(`${this.url}/${templateId}/custom`, template)
		) as Promise<ICommunicationTemplate>;
	}

	public createCustom(template: Partial<ICommunicationTemplate>): Promise<ICommunicationTemplate> {
		return this.process(this.api.post(`${this.url}/custom`, template)) as Promise<ICommunicationTemplate>;
	}

	public deleteCustom(templateId: string): Promise<ICommunicationTemplate> {
		return this.process(this.api.delete(`${this.url}/${templateId}/custom`)) as Promise<ICommunicationTemplate>;
	}
}
